import { RANGE_DASH } from "@/helpers/statFormat"
import { ICurrencyEntry } from "@/store/apis/catalogsAPI/catalogs.types"

const AMOUNT_PATTERN = /^(0|[1-9][0-9]*)$/

export const parseAmount = (text: string): bigint | null =>
  AMOUNT_PATTERN.test(text) ? BigInt(text) : null

export const compareAmounts = (a: bigint, b: bigint): -1 | 0 | 1 =>
  a < b ? -1 : a > b ? 1 : 0

export const hasValue = (text: string): boolean => {
  const amount = parseAmount(text)
  return amount !== null && amount > 0n
}

const NUMBER_FORMATS = new Map<string, Intl.NumberFormat>()
const DECIMAL_SEPARATORS = new Map<string, string>()

export const numberFormatOf = (locale: string): Intl.NumberFormat => {
  const cached = NUMBER_FORMATS.get(locale)
  if (cached) {
    return cached
  }
  const created = new Intl.NumberFormat(locale)
  NUMBER_FORMATS.set(locale, created)
  return created
}

export const decimalSeparatorOf = (locale: string): string => {
  const cached = DECIMAL_SEPARATORS.get(locale)
  if (cached !== undefined) {
    return cached
  }
  const separator =
    numberFormatOf(locale)
      .formatToParts(1.5)
      .find((part) => part.type === "decimal")?.value ?? "."
  DECIMAL_SEPARATORS.set(locale, separator)
  return separator
}

export const buildScaleIndex = (
  currencies: readonly ICurrencyEntry[]
): ReadonlyMap<string, number> =>
  new Map(currencies.map((currency) => [currency.code, currency.scale]))

/** `amount` is in minor units; the text shows exactly `scale` fraction digits. `null` for a malformed amount. */
export const formatMoney = (
  amount: string,
  scale: number,
  locale: string
): string | null => {
  const parsed = parseAmount(amount)
  if (parsed === null) {
    return null
  }
  const unit = 10n ** BigInt(scale)
  const wholeText = numberFormatOf(locale).format(parsed / unit)
  if (scale === 0) {
    return wholeText
  }
  const fraction = (parsed % unit).toString().padStart(scale, "0")
  return `${wholeText}${decimalSeparatorOf(locale)}${fraction}`
}

export const formatMoneyRange = (
  min: string,
  max: string,
  scale: number,
  locale: string
): string | null => {
  const minText = formatMoney(min, scale, locale)
  const maxText = formatMoney(max, scale, locale)
  if (minText === null || maxText === null) {
    return null
  }
  return min === max ? minText : `${minText}${RANGE_DASH}${maxText}`
}

const PLURAL_STABLE_BELOW = 100n

/** A small number with the same `Intl.PluralRules` category as `amount` in en / ru / uk, safe past 2^53. */
export const pluralCount = (amount: bigint): number =>
  amount < PLURAL_STABLE_BELOW
    ? Number(amount)
    : 100 + Number(amount % PLURAL_STABLE_BELOW)

const FRACTION_OFFSET = 0.5

/** A plural count for `amount` minor units as `formatMoney` shows them: at `scale > 0` the text always carries fraction digits, so the count is fractional. */
export const pluralCountAtScale = (amount: bigint, scale: number): number =>
  scale === 0
    ? pluralCount(amount)
    : pluralCount(amount / 10n ** BigInt(scale)) + FRACTION_OFFSET
